import { useMutation } from '@apollo/client';
import { Background, SpinnerButton, Text, TextInput } from '@do/walrus';
import { Form, Formik } from 'formik';
import _get from 'lodash/get';
import React, { ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as Yup from 'yup';
import defaultBg from '../assets/backgrounds/Login_Default.jpg';
import newBrandBg from '../assets/backgrounds/new-brand-bg.png';
import copy from '../copy';
import {
  forgotPassword as ForgotPassword,
  forgotPasswordVariables
} from '../generated-types/forgotPassword';
import useDocumentTitle from '../hooks/use-document-title';
import { FORGOT_PASSWORD } from '../queries/queries';
import { handleFormError } from '../utils/gql-utils';
import { ErrorBanner, FormWrap, PaddedContents, Wrap } from './Common';
import Header from './Header';
import NewErrorBanner from './newBrandDesign/ErrorBanner';
import NewFormWrap from './newBrandDesign/FormWrap';
import NewSpinnerButton from './newBrandDesign/SpinnerButton';
import NewText from './newBrandDesign/Text';
import NewTextInput from './newBrandDesign/TextInput';
import NewTitle from './newBrandDesign/Title';

const Title = styled.h4``;

const passwordForgotValidation = Yup.object().shape({
  email: Yup.string()
    .email(copy.form.validation.email_valid)
    .required(copy.form.validation.email_required)
});

const PasswordForgot: React.FC<{ isNewBrandDesignEnabled?: boolean }> = ({
  isNewBrandDesignEnabled = false
}): ReactElement => {
  const FormWrapComponent = isNewBrandDesignEnabled ? NewFormWrap : FormWrap;
  const TitleComponent = isNewBrandDesignEnabled ? NewTitle : Title;
  const TextComponent = isNewBrandDesignEnabled ? NewText : Text;
  const TextInputComponent = isNewBrandDesignEnabled ? NewTextInput : TextInput;
  const ErrorBannerComponent = isNewBrandDesignEnabled
    ? NewErrorBanner
    : ErrorBanner;
  const SpinnerButtonComponent = isNewBrandDesignEnabled
    ? NewSpinnerButton
    : SpinnerButton;

  const location = useLocation();

  const [genericServerError, setGenericServerError] = React.useState(null);
  const [forgotPassword, { loading: isLoading }] = useMutation<
    ForgotPassword,
    forgotPasswordVariables
  >(FORGOT_PASSWORD);
  const navigate = useNavigate();

  useDocumentTitle(copy.form.password_forgot_title);

  return (
    <>
      <Header isLogin={true} />
      <Background url={isNewBrandDesignEnabled ? newBrandBg : defaultBg} />
      <Wrap>
        <FormWrapComponent>
          <PaddedContents>
            <TitleComponent>{copy.form.password_forgot.title}</TitleComponent>
            <TextComponent>{copy.form.password_forgot.details}</TextComponent>
            <Formik
              initialValues={{
                email: _get(location, 'state.email') || ''
              }}
              validationSchema={passwordForgotValidation}
              onSubmit={(values, { setErrors }) => {
                setGenericServerError(null); /* Reset displayed error */
                forgotPassword({
                  variables: {
                    forgotPasswordRequest: values
                  }
                })
                  .then(() => {
                    navigate('/login', { state: { success: 'forgot' } });
                  })
                  .catch(handleFormError(setErrors, setGenericServerError));
              }}
            >
              {({ errors, values, touched }) => (
                <Form>
                  {genericServerError && (
                    <ErrorBannerComponent error={genericServerError} />
                  )}

                  <TextInputComponent
                    type="email"
                    placeholder={copy.form.password_forgot.placeholder}
                    name="email"
                    required
                    label={copy.form.password_forgot.label}
                    touched={touched.email}
                    error={errors.email}
                    value={values.email}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                  />
                  <SpinnerButtonComponent
                    className={isNewBrandDesignEnabled ? '' : 'do-u-mt--medium'}
                    type="submit"
                    fullWidth
                    spinning={isLoading}
                    disabled={isLoading}
                    loadingMessage={copy.form.password_forgot.loading}
                  >
                    {copy.form.password_forgot.button_text}
                  </SpinnerButtonComponent>
                </Form>
              )}
            </Formik>
          </PaddedContents>
        </FormWrapComponent>
      </Wrap>
    </>
  );
};

export default PasswordForgot;
